import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { Badge, Input, Tabs, Select, InputRef, Spin } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CategoryFilter,
  SearchResultData,
  SearchResultDataItem,
  SearchModuleResponse,
  GlobalSearchPropsExtended,
} from '../../interfaces/global-search';
import CloseableModal from '../common/common-componnets/closeable-modal.component';
import './styles.css';
import { getCategoryMeta, getExcludeSearchDomain } from './data';
import { getModuleByRole } from '../../helpers/get-module-by-role';
import { useRoleGetter } from '../../helpers/getUserInfo';
import SearchResults from './search-results';
import { normalizeApiKey } from './data';
import { useGetSearchMutation } from '../../redux/features/search/search-api';
import { useDebounce } from '../../hooks/use-debounce';
import { Module } from '../../utilities/enums';
import { capitalizeText } from '../common/common-functions/common-functions';

type SearchDataWithSize = Record<string, SearchResultData>;

const GlobalSearch = ({
  visible,
  onClose,
  loggedInUser,
  searchQuery,
  setSearchQuery,
}: GlobalSearchPropsExtended) => {
  const role = useRoleGetter();
  const modules = useMemo(
    () => getModuleByRole(loggedInUser?.rolet).filter(item => item.activeRole),
    [loggedInUser?.rolet],
  );

  const [activeCategory, setActiveCategory] = useState<CategoryFilter>('All');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedModule, setSelectedModule] = useState<Module>();
  const searchInputRef = useRef<InputRef | null>(null);

  const [searchData, setSearchData] = useState<SearchDataWithSize>({});

  const [getSearch, { isLoading, error }] = useGetSearchMutation();
  const debouncedSearchQuery = useDebounce(searchQuery, 600);
  const numberOfCharacters = 3;

  const needsMoreCharacters =
    debouncedSearchQuery.trim().length < numberOfCharacters;

  useEffect(() => {
    const fetchSearchResults = () => {
      if (!needsMoreCharacters) {
        const excludeSearchSubModule = getExcludeSearchDomain(activeCategory);

        getSearch({
          searchWord: debouncedSearchQuery.trim(),
          searchModule: selectedModule,
          ...(excludeSearchSubModule && { excludeSearchSubModule }),
        })
          .unwrap()
          .then(response => {
            const normalizedData: SearchDataWithSize = {};
            const moduleData = Object.values(
              response,
            )[0] as unknown as SearchModuleResponse;

            if (moduleData && moduleData.subModules) {
              moduleData.subModules.forEach(subModule => {
                const normalizedKey = normalizeApiKey(
                  subModule.searchSubModule,
                );

                if (normalizedKey && Array.isArray(subModule.records)) {
                  normalizedData[normalizedKey] = {
                    records: subModule.records,
                    size: subModule.size,
                  };
                }
              });
            }

            setSearchData(normalizedData);
          })
          .catch(() => setSearchData({}));
      } else {
        setSearchData({});
        setActiveCategory('All');
      }
    };

    fetchSearchResults();
  }, [debouncedSearchQuery, needsMoreCharacters, selectedModule]);

  useEffect(() => {
    if (visible) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);

      if (role) {
        localStorage.setItem('moduleUser', role);
      }
    } else {
      localStorage.removeItem('moduleUser');
    }
  }, [visible, role]);

  useEffect(() => {
    if (modules.length === 1) {
      setSelectedModule(modules[0].value);
    } else if (modules.length > 1) {
      setSelectedModule(modules[modules.length - 1].value);
    }
  }, [modules.length]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    let totalCount = 0;
    const records: Record<string, SearchResultDataItem[]> = {};

    Object.entries(searchData).forEach(([key, value]) => {
      if (value.size > 0) {
        const count = value.size;
        counts[key] = count;
        totalCount += count;
        records[key] = value.records;
      }
    });

    return { counts, totalCount, records };
  }, [searchData]);

  const getCategoryCount = (category: string) => {
    if (category === 'All') return categoryCounts.totalCount;
    return categoryCounts.counts[category] || 0;
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setSelectedIndex(0);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleCategoryChange = (key: string) => {
    setActiveCategory(key as CategoryFilter);
    setSelectedIndex(0);
  };

  const categories = useMemo(() => {
    return Object.keys(categoryCounts.counts).sort((a, b) => {
      if (a.includes('DOKUMENT')) return 1;
      if (b.includes('DOKUMENT')) return -1;
      return 0;
    });
  }, [categoryCounts.counts]);

  const tabItems = useMemo(() => {
    return ['All', ...categories].map(key => {
      const count = getCategoryCount(key);
      return {
        key,
        label: (
          <span className="tab-label">
            {key === 'All' ? (
              <>
                <span>Të gjitha</span>
                <Badge
                  count={count}
                  style={{
                    backgroundColor: '#f0f0f0',
                    color: '#595959',
                    marginLeft: '8px',
                  }}
                />
              </>
            ) : (
              <>
                <span>{capitalizeText(getCategoryMeta(key).label, true)}</span>
                <Badge
                  count={count}
                  style={{
                    backgroundColor: '#f0f0f0',
                    color: '#595959',
                    marginLeft: '8px',
                  }}
                />
              </>
            )}
          </span>
        ),
        children: null,
      };
    });
  }, [categoryCounts.counts]);

  return (
    <CloseableModal
      visible={visible}
      onCancel={onClose}
      title={null}
      width={1200}
      bodyStyle={{
        padding: 0,
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="global-search-container">
        <div className="search-sticky-header">
          <div className="search-header">
            <div className="flex items-center gap-2">
              <Input
                ref={searchInputRef}
                className="h-9"
                placeholder="Kërko në të gjithë sistemin..."
                prefix={<SearchOutlined className="search-icon" />}
                suffix={
                  <>
                    {isLoading && <Spin size="small" />}
                    {searchQuery && !isLoading && (
                      <CloseOutlined
                        className="clear-icon"
                        onClick={handleClearSearch}
                      />
                    )}
                  </>
                }
                value={searchQuery}
                onChange={handleSearchChange}
                size="large"
                style={{ flex: 1 }}
              />
              <Select
                autoClearSearchValue={false}
                placeholder="Zgjidhni modulin"
                value={selectedModule}
                onChange={setSelectedModule}
                disabled={modules.length === 1}
                options={modules.map(module => ({
                  label: module.label,
                  value: module.value,
                }))}
                className="h-9 min-w-[200px]"
              />
            </div>
          </div>

          <div className="search-tabs">
            <Tabs
              activeKey={activeCategory}
              onChange={handleCategoryChange}
              items={tabItems}
              size="large"
            />
          </div>
        </div>

        <div className="search-scrollable-content">
          <div className="search-results">
            <SearchResults
              activeCategory={activeCategory}
              onClose={onClose}
              searchData={categoryCounts.records}
              selectedIndex={selectedIndex}
              searchQuery={searchQuery}
              needsMoreCharacters={needsMoreCharacters}
              isLoading={isLoading}
              error={error}
              numberOfCharacters={numberOfCharacters}
              selectedModule={selectedModule}
            />
          </div>
        </div>
      </div>
    </CloseableModal>
  );
};

export default GlobalSearch;
